import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
  Grid,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemButton
} from '@mui/material';
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  WhatsApp as WhatsAppIcon,
  Sms as SmsIcon,
  AutoAwesome as SparklesIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  ArrowDropDown as ArrowDropDownIcon
} from '@mui/icons-material';
import { useMerchantAuth } from '../../../contexts/MerchantAuthContext';
import FlutterServicesAPI from '../../../services/flutterServices';
import useRouteMeta from '../../../hooks/useRouteMeta';
// import WhatsAppSignUp from './WhatsAppSignUp';
import { merchantAuthAPI } from '../../../services/merchantAPI';
import { masterDataConfigAPI } from '../../../services/api';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

const MerchantLogin = () => {
  // SEO Meta Tags - Automatically applied from config
  useRouteMeta();

  const [contact, setContact] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({
    contact: ''
  });

  // Country code state
  const [countries, setCountries] = useState([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState('+91');
  const [selectedCountryId, setSelectedCountryId] = useState(null);
  const [countrySearch, setCountrySearch] = useState('');
  const [countryAnchorEl, setCountryAnchorEl] = useState(null);
  const searchInputRef = useRef(null);
  const selectedCountryRef = useRef(null);

  const { isAuthenticated } = useMerchantAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if running in Flutter app
  const isFlutterApp = FlutterServicesAPI.isFlutterApp();

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const [showWhatsAppSmsAuth, setShowWhatsAppSmsAuth] = useState(true);

  useEffect(() => {
    const checkAuthVisibility = async () => {
      try {
        const response = await merchantAuthAPI.checkAuthVisibility();
        if (response.data && typeof response.data.show_whatsapp_sms_auth === 'boolean') {
          setShowWhatsAppSmsAuth(response.data.show_whatsapp_sms_auth);
          console.log('🔐 Auth visibility check:', response.data.show_whatsapp_sms_auth);
        }
      } catch (error) {
        console.error('❌ Failed to check auth visibility:', error);
        setShowWhatsAppSmsAuth(true); // Default to showing auth options
      }
    };
    checkAuthVisibility();
  }, []);

  // Load countries and detect default country from IP
  useEffect(() => {
    const loadCountriesAndDetect = async () => {
      try {
        // Load country list from master data
        const countriesResponse = await masterDataConfigAPI.getCountries();
        const countryList = countriesResponse.data.data || [];
        setCountries(countryList);

        // Detect country from IP
        try {
          const detectResponse = await merchantAuthAPI.detectCountry();
          if (detectResponse.data && detectResponse.data.country_code) {
            setSelectedCountryCode(detectResponse.data.country_code);
            setSelectedCountryId(detectResponse.data.country_id);
          }
        } catch (detectErr) {
          console.error('❌ Failed to detect country from IP:', detectErr);
          // Fallback: find India in country list
          const india = countryList.find(c => c.country_code === '+91');
          if (india) {
            setSelectedCountryId(india.id);
          }
        }
      } catch (error) {
        console.error('❌ Failed to load countries:', error);
      }
    };
    loadCountriesAndDetect();
  }, []);

  const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  // Check if contact looks like a phone number (not email and contains digits)
  const isPhoneInput = contact.trim().length > 0 && !contact.includes('@') && /\d/.test(contact);

  // Detect country code from phone number using libphonenumber-js and DB lookup
  useEffect(() => {
    const detectCountryFromPhone = async () => {
      const contactValue = contact.trim();
      // Only trigger if it's a phone number (not email) and has at least 5 digits
      if (isPhoneInput && !contact.includes('@') && contactValue.replace(/\D/g, '').length >= 5) {
        try {
          // Extract mobile number (digits only) for DB lookup
          const mobileNumber = contactValue.replace(/\D/g, '');

          // Step 1: Check DB for existing mobile number and get stored country code
          try {
            const dbResponse = await merchantAuthAPI.detectCountryFromPhone(mobileNumber);
            if (dbResponse.data && dbResponse.data.country_code) {
              const dbCountryCode = dbResponse.data.country_code;
              const dbCountryId = dbResponse.data.country_id;
              const dbCountryName = dbResponse.data.country_name;

              // Update to DB country code if different
              if (dbCountryCode !== selectedCountryCode) {
                setSelectedCountryCode(dbCountryCode);
                if (dbCountryId) {
                  setSelectedCountryId(dbCountryId);
                }
                console.log('📱 DB detected country from mobile:', dbCountryName, dbCountryCode);
              }
              return; // DB lookup takes priority, skip libphonenumber-js detection
            }
          } catch (dbErr) {
            console.log('📱 DB lookup failed, falling back to libphonenumber-js:', dbErr?.response?.status);
          }

          // Step 2: Fallback to libphonenumber-js if DB lookup fails
          let phoneNumber = null;
          const countryCodeWithoutPlus = selectedCountryCode.replace('+', '');

          // Try parsing as international if it starts with +
          if (contactValue.startsWith('+')) {
            phoneNumber = parsePhoneNumberFromString(contactValue);
          }

          // If no result, check if number already has country code embedded and prepend + only
          if (!phoneNumber && contactValue.startsWith(countryCodeWithoutPlus)) {
            phoneNumber = parsePhoneNumberFromString('+' + contactValue);
          }

          // If still no result, try parsing with the current selected country code
          if (!phoneNumber) {
            const withCountryCode = selectedCountryCode + contactValue;
            phoneNumber = parsePhoneNumberFromString(withCountryCode);
          }

          if (phoneNumber && phoneNumber.isValid()) {
            const detectedCountryCode = `+${phoneNumber.countryCallingCode}`;
            const detectedCountryISO = phoneNumber.country; // e.g., "BM" for Bermuda, "US" for USA
            // Try to find country by country_slug (ISO code) first, then by country code
            let detectedCountry = countries.find(c => c.country_slug === detectedCountryISO);
            if (!detectedCountry) {
              detectedCountry = countries.find(c => c.country_code === detectedCountryCode);
            }
            // Update if country code is different OR ISO code is different (handles same country code for different countries)
            if (detectedCountry && (detectedCountryCode !== selectedCountryCode || detectedCountry.id !== selectedCountryId)) {
              console.log('🌍 Country update detected - Current ID:', selectedCountryId, 'Code:', selectedCountryCode, '→ New ID:', detectedCountry.id, 'Code:', detectedCountryCode, 'Name:', detectedCountry.country_name);
              setSelectedCountryCode(detectedCountryCode);
              setSelectedCountryId(detectedCountry.id);
              console.log('🌍 Auto-detected country from phone (libphonenumber-js):', detectedCountry.country_name, detectedCountryISO, 'Code:', detectedCountryCode);
            } else {
              console.log('🌍 Country unchanged - ID:', selectedCountryId, 'Code:', selectedCountryCode);
            }
          }
        } catch (error) {
          console.error('❌ Failed to detect country from phone:', error);
        }
      }
    };

    const timer = setTimeout(detectCountryFromPhone, 500);
    return () => clearTimeout(timer);
  }, [contact, selectedCountryCode, countries]);

  const isValidContact = () => {
    const contactValue = contact.trim();
    if (!contactValue) return false;
    if (isEmail(contactValue)) return true;

    console.log('🔍 isValidContact - contactValue:', contactValue);
    console.log('🔍 isValidContact - selectedCountryCode:', selectedCountryCode);

    // Reject inputs with letters (except valid email which is already handled)
    const hasLetters = /[a-zA-Z]/.test(contactValue);
    if (hasLetters) {
      console.log('🔍 isValidContact - rejected: contains letters');
      return false;
    }

    // Use libphonenumber-js for robust phone validation
    try {
      let phoneNumber = null;
      const countryCodeWithoutPlus = selectedCountryCode.replace('+', '');

      // Try parsing as international if it starts with +
      if (contactValue.startsWith('+')) {
        phoneNumber = parsePhoneNumberFromString(contactValue);
        console.log('🔍 isValidContact - attempt 1 (with +):', phoneNumber);
      }

      // If no result, check if number already has country code embedded and prepend + only
      if (!phoneNumber && contactValue.startsWith(countryCodeWithoutPlus)) {
        phoneNumber = parsePhoneNumberFromString('+' + contactValue);
        console.log('🔍 isValidContact - attempt 2 (embedded country code +):', phoneNumber);
      }

      // If still no result, prepend country code and try as international
      if (!phoneNumber) {
        const withCountryCode = selectedCountryCode + contactValue;
        phoneNumber = parsePhoneNumberFromString(withCountryCode);
        console.log('🔍 isValidContact - attempt 3 (with country code):', phoneNumber);
      }

      const isValid = phoneNumber && phoneNumber.isValid();
      console.log('🔍 isValidContact - isValid:', isValid);
      return isValid;
    } catch (error) {
      console.error('❌ isValidContact - error:', error);
      return false;
    }
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    const contactValue = contact.trim();

    if (!contactValue) {
      setFieldErrors({ contact: 'Please enter a valid whatsapp number or email' });
      return;
    }

    // Determine if user is attempting email or phone
    const looksLikeEmail = contactValue.includes('@') || isEmail(contactValue);

    setError('');
    setFieldErrors({ contact: '' });
    setLoading(true);

    try {
      if (looksLikeEmail) {
        // Validate Email
        if (!isEmail(contactValue)) {
          setFieldErrors({ contact: 'Please enter a valid whatsapp number or email' });
          setLoading(false);
          return;
        }

        // Send OTP via Email
        try {
          await merchantAuthAPI.sendEmailLoginOTP(contactValue);
          navigate('/verify-otp', { state: { contact: contactValue, contactType: 'email' } });
        } catch (emailErr) {
          if (emailErr?.response?.status === 404) {
            navigate('/register', { state: { prefillContact: contactValue, contactType: 'email' } });
          } else {
            const msg = emailErr?.response?.data?.error || emailErr?.response?.data?.message || 'Failed to send OTP. Please try again.';
            setError(msg);
          }
          setLoading(false);
          return;
        }
      } else {
        // Reject inputs with letters (except valid email which is already handled)
        const hasLetters = /[a-zA-Z]/.test(contactValue);
        if (hasLetters) {
          setFieldErrors({ contact: 'Please enter a valid whatsapp number or email' });
          setLoading(false);
          return;
        }

        // Use libphonenumber-js to parse and validate phone number
        let phoneNumber;
        try {
          // Try parsing as international if it starts with +
          if (contactValue.startsWith('+')) {
            phoneNumber = parsePhoneNumberFromString(contactValue);
          }

          // If no result, prepend country code and try as international
          // Skip embedded code check for single-digit country codes (like +7) to avoid ambiguity
          if (!phoneNumber) {
            const withCountryCode = selectedCountryCode + contactValue;
            phoneNumber = parsePhoneNumberFromString(withCountryCode);
          }
        } catch (error) {
          setFieldErrors({ contact: 'Please enter a valid whatsapp number or email' });
          setLoading(false);
          return;
        }

        if (!phoneNumber || !phoneNumber.isValid()) {
          setFieldErrors({ contact: 'Please enter a valid whatsapp number or email' });
          setLoading(false);
          return;
        }

        // Extract country code and national number from parsed phone
        const detectedCountryCode = phoneNumber.countryCallingCode;
        const nationalNumber = phoneNumber.nationalNumber;

        // Update selected country code if different from detected
        if (detectedCountryCode !== selectedCountryCode.replace('+', '')) {
          setSelectedCountryCode(`+${detectedCountryCode}`);
        }

        // Send OTP via the unified endpoint with detected country code and national number
        try {
          await merchantAuthAPI.sendLoginOTP({ mobile: nationalNumber, country_code: `+${detectedCountryCode}` });
          navigate('/verify-otp', { state: { contact: nationalNumber, contactType: 'phone', country_code: `+${detectedCountryCode}` } });
        } catch (otpErr) {
          const isNotFound =
            otpErr?.response?.status === 404 ||
            otpErr?.response?.data?.code === 'USER_NOT_FOUND';

          if (isNotFound) {
            navigate('/register', { state: { prefillContact: nationalNumber, contactType: 'phone', country_code: `+${detectedCountryCode}`, country_id: selectedCountryId } });
          } else {
            const msg = otpErr?.response?.data?.error || otpErr?.response?.data?.message || 'Failed to send OTP. Please try again.';
            setError(msg);
          }
          setLoading(false);
          return;
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Failed to send OTP. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    const isMobile = window.innerWidth < 600; // xs breakpoint
    if (isMobile) {
      document.body.style.backgroundColor = 'white';
    } else {
      document.body.style.backgroundColor = '#f5f5f5';
    }

    return () => {
      document.body.style.backgroundColor = '#f5f5f5';
    };
  }, []);

  // Country dropdown handlers
  const handleCountryClick = (event) => {
    setCountryAnchorEl(event.currentTarget);
    setCountrySearch('');
  };

  const handleCountryClose = () => {
    setCountryAnchorEl(null);
    setCountrySearch('');
  };

  const handleCountrySelect = (country) => {
    console.log('🌍 Selected country:', country.name, 'ID:', country.id, 'Code:', country.country_code);
    setSelectedCountryCode(country.country_code);
    setSelectedCountryId(country.id);
    handleCountryClose();
  };

  // Focus search input and scroll to selected country when popover opens
  useEffect(() => {
    if (countryAnchorEl) {
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
        if (selectedCountryRef.current) {
          selectedCountryRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [countryAnchorEl]);

  // Filter countries based on search
  const filteredCountries = countries.filter((c) => {
    if (!countrySearch) return true;
    const search = countrySearch.toLowerCase();
    return (
      (c.country_name && c.country_name.toLowerCase().includes(search)) ||
      (c.country_code && c.country_code.includes(search))
    );
  });

  const countryPopoverOpen = Boolean(countryAnchorEl);

  return (
    <Container component="main" maxWidth={false}
      sx={{ maxWidth: '560px', mx: 'auto', minHeight: '100vh', display: 'flex', flexDirection: 'column' }} data-testid="merchant-login-page">
      <Box
        sx={{
          marginTop: { xs: 0.5, sm: 8 },
          marginBottom: { xs: 1, sm: 0 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexGrow: 1,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            pt: { xs: 2, sm: 4, md: 6 },
            pb: { xs: 3, sm: 5, md: 7 },
            pl: { xs: 2, sm: 4, md: 6 },
            pr: { xs: 2, sm: 3, md: 5 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: '18px',
            width: '100%',
            boxShadow: { xs: 'none', sm: 3 },
            border: { xs: 'none', sm: '1px solid #E0E0E0' },
            minHeight: '80vh',

            '@media (min-width: 414px)': {
              pt: 2,
              pb: 2,
              pl: 4,
              pr: 4,
            }
          }}
        >
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
            {/* Glammify Logo */}
            <Box
              component="img"
              src="/assets/img/gl-logo.svg"
              alt="Glammify Logo"
              sx={{
                maxWidth: { xs: '150px', sm: '180px' },
                height: 'auto',
                width: '100%',
                marginBottom: '50px',
                marginTop: '30px'
              }}
            />

            <Box component="form" onSubmit={handleSendCode} sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', mb: 1 }}>
                <Typography variant="body2" className="form-label">
                  {showWhatsAppSmsAuth ? 'Whatsapp Number/Email' : 'Email'} <span style={{ color: '#A864ED' }}>*</span>
                </Typography>
              </Box>
              <TextField
                fullWidth
                id="contact"
                placeholder={showWhatsAppSmsAuth ? 'Whatsapp Number/Email' : 'Email'}
                name="contact"
                sx={{
                  '& .MuiInputBase-input': {
                    padding: isPhoneInput
                      ? '0 19px 0 0px !important'
                      : '0 19px !important'
                  }
                }}
                autoFocus
                value={contact}
                onChange={(e) => {
                  setContact(e.target.value);
                  setError('');
                  setFieldErrors({ contact: '' });
                }}
                disabled={loading}
                error={!!fieldErrors.contact}
                helperText={fieldErrors.contact}
                className="form-control"
                InputProps={{
                  startAdornment:
                    isPhoneInput ? (
                      <InputAdornment position="start">
                        <Box
                          onClick={handleCountryClick}
                          sx={{
                            backgroundColor: '#f3f3f3',
                            px: '10px',
                            py: '5px',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            userSelect: 'none',
                            '&:hover': {
                              backgroundColor: '#e8e8e8',
                            }
                          }}
                        >
                          <Typography sx={{ color: 'text.primary', fontSize: '14px', fontFamily: 'var(--font-montserrat-medium)' }}>
                            {selectedCountryCode}
                          </Typography>
                          <ArrowDropDownIcon sx={{ fontSize: 18, color: '#666', ml: '2px' }} />
                        </Box>
                      </InputAdornment>
                    ) : null,
                }}
              />

              {/* Country Code Popover */}
              <Popover
                open={countryPopoverOpen}
                anchorEl={countryAnchorEl}
                onClose={handleCountryClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                PaperProps={{
                  sx: {
                    width: '300px',
                    maxHeight: '350px',
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    mt: 0.5,
                  }
                }}
              >
                {/* Search field */}
                <Box sx={{ p: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                  <TextField
                    inputRef={searchInputRef}
                    fullWidth
                    size="small"
                    placeholder="Search country..."
                    value={countrySearch}
                    onChange={(e) => setCountrySearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 18, color: '#9e9e9e' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontFamily: 'var(--font-montserrat-medium)',
                      }
                    }}
                  />
                </Box>
                {/* Country list */}
                <List sx={{ p: 0, overflow: 'auto', maxHeight: '280px' }}>
                  {filteredCountries.map((country) => (
                    <ListItemButton
                      key={country.id}
                      ref={country.id === selectedCountryId ? selectedCountryRef : null}
                      onClick={() => handleCountrySelect(country)}
                      selected={country.id === selectedCountryId}
                      sx={{
                        py: 0.8,
                        px: 2,
                        '&.Mui-selected': {
                          backgroundColor: '#f3e8ff',
                        },
                        '&:hover': {
                          backgroundColor: '#f9f5ff',
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography sx={{ fontSize: '13px', fontFamily: 'var(--font-montserrat-medium)', color: '#1A1A1A' }}>
                            <span style={{ display: 'inline-block', width: '45px', textAlign: 'left' }}>{country.country_code}</span>
                            {country.country_name}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  ))}
                  {filteredCountries.length === 0 && (
                    <ListItem sx={{ py: 2 }}>
                      <ListItemText
                        primary={
                          <Typography sx={{ fontSize: '13px', color: '#9e9e9e', textAlign: 'center' }}>
                            No countries found
                          </Typography>
                        }
                      />
                    </ListItem>
                  )}
                </List>
              </Popover>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                className="btn-primary"
                sx={{ mt: 1, mb: 1 }}
                disabled={loading || !contact.trim()}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Login With Code'
                )}
              </Button>

              <Box sx={{ textAlign: 'center', mt: 1, mb: 1 }}>
                <Typography className="login-text" variant="body2">
                  New to <span style={{ color: '#A864ED' }}>Glammify?</span>
                </Typography>
              </Box>

              <Button
                fullWidth
                variant="contained"
                onClick={() => navigate('/register')}
                className="btn-primary"
                sx={{ mt: 1, mb: 1 }}
              >
                Create Account
              </Button>

              {/* Continue as Guest Button - Required for App Store Compliance */}
              {isFlutterApp && (
                <Typography
                  onClick={() => navigate('/guest-gallery')}
                  sx={{
                    display: 'block',
                    textAlign: 'center',
                    mt: 1,
                    mb: 1,
                    fontSize: 'var(--font-size-small)',
                    fontFamily: 'var(--font-montserrat-semibold) !important',
                    color: 'rgb(168, 100, 237)',
                    fontWeight: 600,
                    textDecoration: 'none',
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Continue as Guest
                </Typography>
              )}
            </Box>

          </Box>

        </Paper>
      </Box>

      {/* Footer Text */}
      <Box sx={{ pb: 3, pt: 2, textAlign: 'center', width: '100%', mt: 'auto' }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: '12px' }}
        >
          Transform your business with AI-generated visuals
        </Typography>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: 'block' }}
        >
          © {new Date().getFullYear()} Glammify. All rights reserved.
        </Typography>
      </Box>
    </Container>
  );
};

export default MerchantLogin;